import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { API } from '@/App';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Download,
  Loader2,
  Search,
  RotateCcw,
  Brain,
  Building2,
  TrendingDown,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Users,
  Package,
  BadgePercent,
  Wallet,
  ArrowUpDown,
  Trophy,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const PurchasesAnalysis = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('potential_savings');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeTab, setActiveTab] = useState('summary');
  const [expandedItems, setExpandedItems] = useState({});

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('يجب أن يكون الملف بصيغة PDF');
      return;
    }

    setIsLoading(true);
    setAnalysisResult(null);
    setLoadingMessage('جاري رفع الملف وتحليله... يرجى الانتظار');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/analysis/pdf/extract-purchases`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      // قراءة الـ response مرة واحدة فقط
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error('فشل في قراءة البيانات من الخادم');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'فشل في تحليل الملف');
      }
      
      if (data.success && data.analysis && data.analysis.length > 0) {
        setAnalysisResult(data);
        const multiSupplierCount = data.summary?.items_with_multiple_suppliers || 0;
        toast.success(`تم تحليل ${data.analysis.length} صنف - ${multiSupplierCount} صنف بموردين متعددين`);
      } else {
        toast.warning(data.message || 'لم يتم العثور على بيانات في الملف');
        setAnalysisResult(null);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    disabled: isLoading
  });

  // ترتيب وفلترة البيانات
  const getFilteredAndSortedItems = () => {
    if (!analysisResult?.analysis) return [];
    
    let filtered = analysisResult.analysis.filter(item => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        item.name?.toLowerCase().includes(query) ||
        item.code?.toLowerCase().includes(query) ||
        item.cheapest_supplier?.toLowerCase().includes(query)
      );
    });

    return filtered.sort((a, b) => {
      let aVal = a[sortBy] || 0;
      let bVal = b[sortBy] || 0;
      
      if (typeof aVal === 'string') {
        return sortOrder === 'asc' 
          ? aVal.localeCompare(bVal, 'ar')
          : bVal.localeCompare(aVal, 'ar');
      }
      
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortOrder === 'desc' ? 
      <ChevronDown className="w-4 h-4 text-purple-600" /> : 
      <ChevronUp className="w-4 h-4 text-purple-600" />;
  };

  const toggleExpand = (code) => {
    setExpandedItems(prev => ({
      ...prev,
      [code]: !prev[code]
    }));
  };

  // تصدير Excel
  const exportToExcel = async () => {
    if (!analysisResult?.analysis?.length) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/analysis/export/supplier-comparison`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          analysis: analysisResult.analysis,
          summary: analysisResult.summary
        })
      });

      if (!response.ok) {
        throw new Error('فشل في تصدير الملف');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `مقارنة_الموردين_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('تم تصدير البيانات');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetAnalysis = () => {
    setAnalysisResult(null);
    setSearchQuery('');
    setExpandedItems({});
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ar-SA', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(num || 0);
  };

  // حساب إحصائيات الموردين
  const getSupplierStats = () => {
    if (!analysisResult?.analysis) return [];
    
    const stats = {};
    analysisResult.analysis.forEach(item => {
      if (item.suppliers_count < 2) return;
      
      item.suppliers?.forEach(supplier => {
        const name = supplier.supplier;
        if (!stats[name]) {
          stats[name] = { items: 0, cheapest: 0, expensive: 0, totalAmount: 0 };
        }
        stats[name].items += 1;
        stats[name].totalAmount += supplier.total_amount;
        
        if (supplier.unit_price === item.cheapest_price) {
          stats[name].cheapest += 1;
        }
        if (supplier.unit_price === item.most_expensive_price) {
          stats[name].expensive += 1;
        }
      });
    });
    
    return Object.entries(stats)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.cheapest - a.cheapest);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Package className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">تحليل مقارنة الموردين</h1>
              <p className="text-sm text-gray-500">تحليل تقارير المشتريات ومقارنة أسعار الموردين واكتشاف التوفير</p>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        {!analysisResult && (
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-2xl p-8 md:p-12 text-center cursor-pointer
              transition-all duration-300 bg-white
              ${isDragActive ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-400'}
              ${isLoading ? 'pointer-events-none opacity-70' : ''}
            `}
          >
            <input {...getInputProps()} />
            
            {isLoading ? (
              <div className="space-y-4">
                <Loader2 className="w-16 h-16 text-purple-500 mx-auto animate-spin" />
                <p className="text-lg font-medium text-gray-700">{loadingMessage}</p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Brain className="w-4 h-4" />
                  <span>يتم التحليل باستخدام GPT-4o</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                  <Upload className="w-10 h-10 text-purple-600" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-gray-700">
                    {isDragActive ? 'أفلت الملف هنا' : 'اسحب ملف PDF هنا'}
                  </p>
                  <p className="text-gray-500 mt-2">ملف تقرير المشتريات من موردين متعددين</p>
                </div>
                <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    PDF فقط
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    مقارنة الموردين
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {analysisResult && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center gap-2 text-gray-500 mb-2">
                  <Package className="w-5 h-5" />
                  <span className="text-sm">إجمالي الأصناف</span>
                </div>
                <p className="text-2xl font-bold text-gray-800">
                  {analysisResult.summary?.total_items_analyzed || 0}
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center gap-2 text-gray-500 mb-2">
                  <Users className="w-5 h-5" />
                  <span className="text-sm">أصناف بموردين متعددين</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {analysisResult.summary?.items_with_multiple_suppliers || 0}
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center gap-2 text-green-500 mb-2">
                  <TrendingDown className="w-5 h-5" />
                  <span className="text-sm">التوفير المحتمل</span>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {formatNumber(analysisResult.summary?.total_potential_savings || 0)}
                </p>
                <p className="text-xs text-gray-400">ريال سعودي</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center gap-2 text-red-500 mb-2">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-sm">الخسارة الفعلية</span>
                </div>
                <p className="text-2xl font-bold text-red-600">
                  {formatNumber(analysisResult.summary?.total_actual_loss || 0)}
                </p>
                <p className="text-xs text-gray-400">ريال سعودي</p>
              </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <div className="flex flex-wrap items-center gap-4">
                {/* Tabs */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab('summary')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeTab === 'summary' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Package className="w-4 h-4 inline-block ml-2" />
                    ملخص الأصناف
                  </button>
                  <button
                    onClick={() => setActiveTab('suppliers')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeTab === 'suppliers' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Trophy className="w-4 h-4 inline-block ml-2" />
                    ترتيب الموردين
                  </button>
                </div>

                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="بحث بالكود أو الاسم أو المورد..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={exportToExcel}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    تصدير Excel
                  </button>
                  <button
                    onClick={resetAnalysis}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    تحليل جديد
                  </button>
                </div>
              </div>
            </div>

            {/* Items Summary Tab */}
            {activeTab === 'summary' && (
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          className="px-4 py-3 text-right text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('code')}
                        >
                          الكود <SortIcon field="code" />
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                          اسم الصنف
                        </th>
                        <th 
                          className="px-4 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('suppliers_count')}
                        >
                          الموردين <SortIcon field="suppliers_count" />
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                          المورد الأرخص
                        </th>
                        <th 
                          className="px-4 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('cheapest_price')}
                        >
                          سعر الأرخص <SortIcon field="cheapest_price" />
                        </th>
                        <th 
                          className="px-4 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('most_expensive_price')}
                        >
                          سعر الأغلى <SortIcon field="most_expensive_price" />
                        </th>
                        <th 
                          className="px-4 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('price_difference_percent')}
                        >
                          فرق % <SortIcon field="price_difference_percent" />
                        </th>
                        <th 
                          className="px-4 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('potential_savings')}
                        >
                          التوفير المحتمل <SortIcon field="potential_savings" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {getFilteredAndSortedItems().map((item) => (
                        <React.Fragment key={item.code}>
                          <tr 
                            className={`hover:bg-gray-50 cursor-pointer ${
                              item.suppliers_count > 1 ? '' : 'opacity-60'
                            }`}
                            onClick={() => item.suppliers_count > 1 && toggleExpand(item.code)}
                          >
                            <td className="px-4 py-3 text-sm font-mono text-gray-600">
                              {item.code}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-800">
                              <div className="flex items-center gap-2">
                                {item.suppliers_count > 1 && (
                                  expandedItems[item.code] ? 
                                    <ChevronUp className="w-4 h-4 text-purple-500" /> :
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                )}
                                {item.name}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.suppliers_count > 1 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-gray-100 text-gray-500'
                              }`}>
                                {item.suppliers_count} مورد
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-center">
                              <div className="flex items-center justify-center gap-1">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span className="text-green-700 truncate max-w-[150px]">
                                  {item.cheapest_supplier}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-green-600 font-semibold">
                                {formatNumber(item.cheapest_price)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-red-600 font-semibold">
                                {formatNumber(item.most_expensive_price)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {item.price_difference_percent > 0 && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                  {item.price_difference_percent.toFixed(1)}%
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {item.potential_savings > 0 && (
                                <span className="text-green-600 font-bold">
                                  {formatNumber(item.potential_savings)}
                                </span>
                              )}
                            </td>
                          </tr>
                          
                          {/* Expanded Suppliers Details */}
                          {expandedItems[item.code] && item.suppliers_count > 1 && (
                            <tr>
                              <td colSpan="8" className="bg-purple-50 p-4">
                                <div className="space-y-2">
                                  <p className="text-sm font-semibold text-purple-700 mb-3">
                                    تفاصيل أسعار الموردين لـ {item.name}:
                                  </p>
                                  <div className="grid gap-2">
                                    {item.suppliers?.map((supplier, idx) => (
                                      <div 
                                        key={idx}
                                        className={`flex items-center justify-between p-3 rounded-lg ${
                                          supplier.unit_price === item.cheapest_price
                                            ? 'bg-green-100 border border-green-200'
                                            : 'bg-white border border-gray-200'
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          {supplier.unit_price === item.cheapest_price ? (
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                          ) : (
                                            <AlertTriangle className="w-5 h-5 text-orange-500" />
                                          )}
                                          <div>
                                            <p className="font-medium text-gray-800">{supplier.supplier}</p>
                                            <p className="text-xs text-gray-500">
                                              الكمية: {supplier.quantity} | الإجمالي: {formatNumber(supplier.total_amount)} ر.س
                                            </p>
                                          </div>
                                        </div>
                                        <div className="text-left">
                                          <p className={`text-lg font-bold ${
                                            supplier.unit_price === item.cheapest_price
                                              ? 'text-green-600'
                                              : 'text-red-600'
                                          }`}>
                                            {formatNumber(supplier.unit_price)} ر.س
                                          </p>
                                          {supplier.unit_price !== item.cheapest_price && (
                                            <p className="text-xs text-red-500">
                                              +{formatNumber(supplier.unit_price - item.cheapest_price)} زيادة
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Suppliers Ranking Tab */}
            {activeTab === 'suppliers' && (
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 w-16">
                          الترتيب
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                          المورد
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                          عدد الأصناف
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                          مرات الأرخص
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                          مرات الأغلى
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                          نسبة التميز
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                          إجمالي المشتريات
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {getSupplierStats().map((supplier, idx) => (
                        <tr key={supplier.name} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-center">
                            {idx === 0 && <Trophy className="w-6 h-6 text-yellow-500 mx-auto" />}
                            {idx === 1 && <span className="text-xl">🥈</span>}
                            {idx === 2 && <span className="text-xl">🥉</span>}
                            {idx > 2 && <span className="text-gray-500">{idx + 1}</span>}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-800">
                            <Building2 className="w-4 h-4 inline-block ml-2 text-gray-400" />
                            {supplier.name}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              {supplier.items}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              {supplier.cheapest}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              {supplier.expensive}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-green-500 h-2 rounded-full"
                                  style={{ width: `${(supplier.cheapest / supplier.items * 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600">
                                {((supplier.cheapest / supplier.items) * 100).toFixed(0)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center font-semibold text-gray-700">
                            {formatNumber(supplier.totalAmount)} ر.س
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchasesAnalysis;
